import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NavigationStart, Router } from '@angular/router';
import { MetadataFormService } from '@app/core/services/metadata/metadata-form.service';
import { Form } from '@app/modules/form-generator/edit-form-v3/models/form.model';
import { ChangeReasonDialogComponent } from '@app/shared/components/change-reason-dialog/change-reason-dialog.component';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { EmployeeTableSecurity } from '@app/shared/models/employee.model';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { FormBuilder, UntypedFormArray, Validators, UntypedFormGroup, FormArray } from '@angular/forms';

@Component({
  selector: 'app-review-plan-goal-type-dialog',
  templateUrl: './review-plan-goal-type-dialog.component.html',
  styleUrls: ['./review-plan-goal-type-dialog.component.scss']
})
export class ReviewPlanGoalTypeDialogComponent implements OnInit {
  @Input() formTitle: string;
  @Input() formId: string;
  @Input() changeReasonFormId: string;
  @Input() formData: any;
  @Output() emitFormData: EventEmitter<void> = new EventEmitter<void>();
  
  getFormData: boolean = false;
  formValid: boolean = false;
  loadingFormDetails: boolean = true;
  formPristine: boolean = true;
  formDetails: Form;
  targetEmployeeTablePermissions: EmployeeTableSecurity[];
  ratingsForm: UntypedFormGroup = this.fb.group({
    ratings: this.fb.array([])
  });
  formGeneratorValid: any;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private metadataFormService: MetadataFormService,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<ReviewPlanGoalTypeDialogComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) data)
  {
    this.formTitle = data.formTitle;
    this.formId = data.formId;
    this.changeReasonFormId = data.changeReasonFormId;
    this.formData = data.formData;
    this.targetEmployeeTablePermissions = data.targetEmployeeTablePermissions;

    this.formData.ratings.forEach((rating) => {
      this.addRating(rating);
    });

    router.events.forEach((event) => {
      if(event instanceof NavigationStart) {
        this.dialogRef.close();
      }
      // NavigationEnd
      // NavigationCancel
      // NavigationError
      // RoutesRecognized
    });
  } 

  get ratings() {
    return this.ratingsForm.controls["ratings"] as FormArray;
  }

  getRatingName(index: number) {
    return this.formData.ratings[index].rating.name;
  }

  addRating(rating: { rating: { id: string, name: string }, value: number }) {
    const ratingForm = this.fb.group({
      id: [rating.rating?.id],
      value: [rating.value, Validators.required]
    });
    this.ratings.push(ratingForm);
  }

  ngOnInit(): void {
    this.getFormDetails();

    this.ratingsForm.statusChanges.subscribe(status => {
      this.formValid = this.formGeneratorValid && this.ratingsForm.valid;
    });
  }

  // Get the form details to find the table assigned to the form 
  getFormDetails() {
    this.loadingFormDetails = true;
    
    this.metadataFormService.getForm(this.formId)
    .pipe(
      finalize(() => {
          this.loadingFormDetails = false
        }
      )
    )
    .subscribe( 
      res => {
        this.formDetails = res; 
      }
    );
  }

  get tableReadAccessObj() {
    return {
      tableId: this.formDetails.table ? this.formDetails.table.id : 'no-table',
      permission: 'read',
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions
    }
  }

  get tableCreateAccessObj() {
    return {
      tableId: this.formDetails.table ? this.formDetails.table.id : 'no-table',
      permission: 'create',
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions
    }
  }

  get tableUpdateAccessObj() {
    return {
      tableId: this.formDetails.table ? this.formDetails.table.id : 'no-table',
      permission: 'update',
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions
    }
  }

  get tableDeleteAccessObj() {
    return {
      tableId: this.formDetails.table ? this.formDetails.table.id : 'no-table',
      permission: 'delete',
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions
    }
  }

  openChangeReasonDialog(formData: any) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      formId: this.changeReasonFormId,
      targetEmployeeTablePermissions: this.targetEmployeeTablePermissions,
    };

    const dialogRef = this.dialog.open(ChangeReasonDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data) {
          this.addChangeReasonsToFormDataAndEmit(formData, data);
        }
      }
    );
  }

  openConfirmCloseDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      text: this.translate.instant('UnsavedChangesMessage')
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.dialogRef.close();
        }
      }
    ); 
  }

  addChangeReasonsToFormDataAndEmit(formData: any, changeReasonFormData: any) {
    let merged = {...formData, ...changeReasonFormData};
    this.emitFormData.emit(merged);
  }

  formDataEmitted(formData) {
    //if there is no change reason form id emit form data, if there is open the change reason dialog
    if(this.changeReasonFormId === undefined){
      this.emitFormData.emit({...formData, ...this.ratingsForm.value});
    }
    else {
      this.openChangeReasonDialog(formData);
    }
  }

  formStatusUpdated(formGeneratorValid) {
    this.formGeneratorValid = formGeneratorValid;
    this.formValid = formGeneratorValid && this.ratingsForm.valid;
  }

  formPristineEmitted(formPristine: boolean) {
    this.formPristine = formPristine;
  }

  close(){
    this.formPristine ? this.dialogRef.close() : this.openConfirmCloseDialog();
  }

}
